/**
 * Client-side generator for the technical indicator variables that crypto_backend
 * learned to calculate (SMA / RSI / Bollinger Bands).
 *
 * crypto_backend computes them in `app/helpers/Indicators/technical_indicators.py`
 * and categorises them in `app/helpers/variable_categorizer.py`, but it exposes no
 * endpoint that lists the resulting variable names. The names that
 * `GET api/get-filter-variables` (common_backend) returns do not contain them, so
 * the filter builder merges these generated names into its Monaco suggestions for
 * crypto filters.
 *
 * Naming convention (must stay in sync with the backend):
 *   {LAYER}_{TIMEFRAME_LABEL}_{INDICATOR}_{PERIOD}_{CUR | PAST1 | PAST2}
 *
 * e.g. L3_1mtf_SMA_20_CUR, L8_1htf_RSI_14_PAST1, L3_1mtf_BB_UPPER_PAST1
 */

export const TIMEFRAME_LAYERS: Record<string, string> = {
  "5s": "L0",
  "15s": "L1",
  "30s": "L2",
  "1m": "L3",
  "3m": "L4",
  "5m": "L5",
  "15m": "L6",
  "30m": "L7",
  "1h": "L8",
  "4h": "L9",
  "1d": "L10",
};

export const TIMEFRAME_LABELS: Record<string, string> = {
  "5s": "5stf",
  "15s": "15stf",
  "30s": "30stf",
  "1m": "1mtf",
  "3m": "3mtf",
  "5m": "5mtf",
  "15m": "15mtf",
  "30m": "30mtf",
  "1h": "1htf",
  "4h": "4htf",
  "1d": "dtf",
};

/** Defaults of `generate_sma_variables` / `generate_rsi_variables` / `generate_atr_variables`. */
export const SMA_PERIODS = [10, 20, 50];
export const RSI_PERIODS = [14];
export const ATR_PERIODS = [14];

/** Bollinger bands are emitted with a fixed period and only CUR + PAST1. */
export const BOLLINGER_PERIOD = 20;
export const BOLLINGER_COMPONENTS = ["UPPER", "MIDDLE", "LOWER", "WIDTH"];
export const BOLLINGER_OFFSETS = ["CUR", "PAST1"];

/** ATR indicator variables only expose CUR + PAST1 (no PAST2). */
export const ATR_OFFSETS = ["CUR", "PAST1"];

const OFFSETS = ["CUR", "PAST1", "PAST2"];

/** Build every indicator variable name available on the crypto backend. */
export function generateIndicatorVariables(): string[] {
  const variables: string[] = [];

  for (const [timeframe, layer] of Object.entries(TIMEFRAME_LAYERS)) {
    const label = TIMEFRAME_LABELS[timeframe];
    if (!label) continue;
    const prefix = `${layer}_${label}`;

    for (const period of SMA_PERIODS) {
      for (const offset of OFFSETS) {
        variables.push(`${prefix}_SMA_${period}_${offset}`);
      }
    }

    for (const period of RSI_PERIODS) {
      for (const offset of OFFSETS) {
        variables.push(`${prefix}_RSI_${period}_${offset}`);
      }
    }

    for (const period of ATR_PERIODS) {
      for (const offset of ATR_OFFSETS) {
        variables.push(`${prefix}_ATR_${period}_${offset}`);
      }
    }

    for (const component of BOLLINGER_COMPONENTS) {
      for (const offset of BOLLINGER_OFFSETS) {
        variables.push(`${prefix}_BB_${component}_${offset}`);
      }
    }
  }

  return variables;
}

export const INDICATOR_VARIABLES: string[] = generateIndicatorVariables();
export const INDICATOR_VARIABLE_SET: Set<string> = new Set(INDICATOR_VARIABLES);

/**
 * Merge the backend-provided variable list with the indicator variables that only
 * the crypto backend understands. Duplicates are dropped, order is preserved.
 */
export function mergeVariableSuggestions(base: string[] | undefined): string[] {
  const merged: string[] = [];
  const seen = new Set<string>();

  for (const variable of [...(base ?? []), ...INDICATOR_VARIABLES]) {
    if (!variable || seen.has(variable)) continue;
    seen.add(variable);
    merged.push(variable);
  }

  return merged;
}
